import { Add, Edit } from "@mui/icons-material";
import {
  Button,
  IconButton,
  Switch,
  Tooltip
} from "@mui/material";
import { useFormik } from "formik";
import moment from "moment";
import { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { API_URLS } from "../../config/APIUrls";
import axiosInstance from "../../config/axios";
import CustomDialog from "../../Shared/CustomDialogBox";
import CustomFilter from "../../Shared/CustomForFiler";
import CustomTable from "../../Shared/CustomTable";
import CustomToPagination from "../../Shared/CustomToPagination";

const DistrictMaster = () => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [page, setPage] = useState(1);
  const initialValue = {
    search: '',
    start_date: '',
    end_date: '',
  };

  const formik = useFormik({
    initialValues: initialValue,
    enableReinitialize: true,
  });

  const { loading, data: districtList } = useQuery(
    ["get_district_master", page, formik.values.search, formik.values.start_date, formik.values.end_date],
    () => {
      return axiosInstance.get(API_URLS.get_district_master, {
        params: {
          search: formik.values.search,
          start_date: formik.values.start_date,
          end_date: formik.values.end_date,
          page: page,
          count: 10,
        },
      });
    },
    {
      refetchOnMount: true,
      refetchOnReconnect: true,
      refetchOnWindowFocus: true

    }
  );

  const districts = districtList?.data?.response || [];

  const fk = useFormik({
    initialValues: {
      name: "",
      desc: "",
    },
    onSubmit: async (values) => {
      if (editData) return await updateDistrict(values);
      else return await createDistrict(values);
    },
  });

  const createDistrict = async (values) => {
    try {
      const reqBody = {
        m04_dist_name: values.name,
        m04_desc: values.desc,
        m04_status: "1",
      };
      const res = await axiosInstance.post(API_URLS.create_district, reqBody);
      toast(res?.data?.msg);
      if (res?.data?.success) {
        queryClient.invalidateQueries("get_district_master");
        handleClose();
      }
    } catch (e) {
      toast.error("Something went wrong while creating");
    }
  };

  const updateDistrict = async (values) => {
    try {
      const reqBody = {
        m04_dist_id: editData.m04_dist_id,
        m04_dist_name: values.name,
        m04_desc: values.desc,
        m04_status: String(editData?.m04_status)
      };
      const res = await axiosInstance.post(API_URLS.update_district, reqBody);
      toast(res?.data?.msg);
      if (res?.data?.success) {
        queryClient.invalidateQueries("get_district_master");
        handleClose();
      }
    } catch (e) {
      toast.error("Something went wrong while updating");
    }
  };

  const toggleStatus = async (row, checked) => {
    try {
      const reqBody = {
        m04_dist_id: row.m04_dist_id,
        m04_status: checked ? "1" : "0"
      };
      const res = await axiosInstance.post(API_URLS.update_district_status, reqBody);
      toast(res?.data?.msg);
      if (res?.data?.success) {
        queryClient.invalidateQueries("get_district_master");
      }
    } catch (e) {
      toast.error("Failed to update status");
    }
  };


  const handleExport = async () => {
    try {
      const res = await axiosInstance.get(API_URLS.district_master_excel, {
        search: formik.values.search || "",
        start_date: formik.values.start_date || "",
        end_date: formik.values.end_date || "",
      })
      const fileUrl = res?.data?.response?.url;
      window.open(fileUrl, "_blank")
    }
    catch (e) {
      console.log("Something went wrong")
    }
  }




  const handleOpen = () => {
    fk.resetForm();
    setEditData(null);
    setOpen(true);
  };

  const handleEdit = (row) => {
    setEditData(row);
    fk.setValues({
      name: row.m04_dist_name,
      desc: row.m04_desc,
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditData(null);
  };

  const tablehead = [
    "S.No",
    "Name",
    "Status",
    "Actions",
    "Date",
  ];

  const tablerow = districts?.data?.map((item, index) => [
    <span>{index + 1}</span>,
    <span>{item?.m04_dist_name || "--"}</span>,
    <Switch
      checked={item?.m04_status === 1}
      onChange={(e) => toggleStatus(item, e.target.checked)}
      color="success"
    />
    ,

    <div className="flex justify-center gap-3">
      <Tooltip title="Edit">
        <IconButton onClick={() => handleEdit(item)}>
          <Edit className="text-blue-600" />
        </IconButton>
      </Tooltip>
    </div>,
    <span>{moment(item?.m04_created_at)?.format("DD-MM-YYYY")}</span>
  ]);

  return (
    <>
      <div className="mx-5">
        <div className="flex justify-between items-center p-4 ">
          <p className="font-bold text-xl">District Master</p>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpen}
            className="!bg-gradient-to-b from-[#7981F9] to-[#5E60D0]"
          >
            Add District
          </Button>
        </div>

        <CustomFilter
          formik={formik}
          onFilter={() => {
            setPage(1);
            queryClient.invalidateQueries("get_district_master");
          }}
          onClear={() => {
            formik.resetForm();
            setPage(1);
            queryClient.invalidateQueries("get_district_master");
          }}
          onExport={handleExport}
        />


        <CustomTable
          tablehead={tablehead}
          tablerow={tablerow}
          isLoading={loading}
        />

        <CustomToPagination
          setPage={setPage}
          page={page}
          data={districts}
        />

      </div>

      <CustomDialog
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={fk.handleSubmit}
        title={editData ? "Edit District" : "Add District"}
        formik={fk}
        fields={[
          { name: "name", label: "Name", type: "text" },
          { name: "desc", label: "Description", type: "text" }
        ]}
      />


    </>
  );
};

export default DistrictMaster;

